import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Megaphone, MapPin, Send, X } from "lucide-react";

// Admin-initiated only: recruitment email goes solely to addresses the admin enters here.
export default function AdminRecruitmentPanel() {
  const [data, setData] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [target, setTarget] = useState(null);
  const [recipients, setRecipients] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/demand/admin/opportunities").then((r) => setData(r.data)).catch(() => setData({ opportunities: [] }));
    api.get("/demand/admin/campaigns").then((r) => setCampaigns(r.data.campaigns)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const send = async () => {
    const list = recipients.split(/[\s,;]+/).filter((e) => e.includes("@"));
    if (list.length === 0) return toast.error("Add at least one cleaner email address");
    setBusy(true);
    try {
      const { data } = await api.post("/demand/admin/recruit", {
        area: target.area, service: target.service, recipients: list, message,
      });
      toast.success(`Recruitment email sent to ${data.sent.length} address(es)`);
      setTarget(null); setRecipients(""); setMessage(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!data) return <Loader2 className="h-6 w-6 animate-spin text-accent" />;

  return (
    <div className="space-y-4" data-testid="admin-recruitment">
      <div>
        <h4 className="font-display font-semibold flex items-center gap-2"><Megaphone className="h-5 w-5 text-accent" /> Cleaner recruitment from demand</h4>
        <p className="text-sm text-muted-foreground mt-0.5">{data.headline} Review an area, then send the invitation yourself — nothing is emailed automatically.</p>
      </div>

      {data.opportunities.length === 0 ? (
        <p className="text-muted-foreground py-8 text-center">No repeated demand recorded yet.</p>
      ) : (
        <div className="space-y-3">
          {data.opportunities.map((o) => (
            <div key={`${o.area}-${o.service}`} className="bg-card rounded-2xl border border-primary/5 p-4" data-testid={`opportunity-${o.area}-${o.service}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 font-medium"><MapPin className="h-4 w-4 text-accent" /> {o.area}</span>
                <span className="text-sm text-muted-foreground capitalize">{(o.service || "").replace(/-/g, " ")}</span>
                <span className="px-2 py-0.5 rounded-full bg-accent/10 text-accent text-xs font-semibold">{o.requests} request{o.requests === 1 ? "" : "s"}</span>
                {o.cleaners_covering_area === 0 && <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold">no cleaners covering</span>}
                <button onClick={() => setTarget(o)} className="ml-auto px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium" data-testid={`recruit-${o.area}-${o.service}`}>
                  Recruit cleaners
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {o.contacts} customer contact(s) captured · {o.notify_me} "notify me" request(s) ·{" "}
                {o.campaigns_sent} campaign(s) sent · last seen {new Date(o.last_seen).toLocaleDateString("en-GB")}
              </p>
            </div>
          ))}
        </div>
      )}

      {target && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center sm:px-5" onClick={() => setTarget(null)}>
          <div className="bg-card rounded-t-2xl sm:rounded-2xl p-5 sm:p-6 w-full sm:max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="recruit-modal">
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-display text-xl font-semibold">Recruit cleaners in {target.area}</h3>
              <button onClick={() => setTarget(null)} className="p-2 rounded-full hover:bg-secondary"><X className="h-4 w-4" /></button>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              We'll tell them customers are requesting {(target.service || "cleaning").replace(/-/g, " ")} in {target.area} ({target.requests} request{target.requests === 1 ? "" : "s"}) and invite them to join free.
            </p>
            <label className="text-sm block mt-4">Cleaner email addresses (comma or newline separated)
              <textarea rows={4} value={recipients} onChange={(e) => setRecipients(e.target.value)} placeholder="cleaner1@example.com, cleaner2@example.com" className="rec-input" data-testid="recruit-recipients" />
            </label>
            <label className="text-sm block mt-3">Personal note (optional)
              <textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Anything you'd like to add about the area or the work" className="rec-input" data-testid="recruit-message" />
            </label>
            <button onClick={send} disabled={busy} className="mt-4 w-full py-3 rounded-full bg-accent text-white font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="recruit-send">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send invitations
            </button>
            <style>{`.rec-input{width:100%;margin-top:0.25rem;padding:0.625rem 0.75rem;border-radius:0.75rem;border:1px solid hsl(var(--border));background:hsl(var(--background));outline:none}`}</style>
          </div>
        </div>
      )}

      {campaigns.length > 0 && (
        <div className="bg-card rounded-2xl border border-primary/5 p-4" data-testid="recruit-campaigns">
          <p className="text-sm font-semibold">Campaigns sent</p>
          <div className="mt-2 space-y-1 text-sm text-muted-foreground">
            {campaigns.map((c) => (
              <p key={c.campaign_id}>
                {c.area} · {(c.service || "").replace(/-/g, " ")} · {c.sent.length} sent
                {c.failed?.length ? `, ${c.failed.length} failed` : ""} ·{" "}
                {new Date(c.created_at).toLocaleDateString("en-GB")}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
